import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { Parameters } from '@atlaskit/editor-common/extensions/extension-parameters';

import { fg } from '@confluence/feature-gating';

import {
	BODIED_EXTENSION_TYPE,
	EXTENSION_TYPE,
	INLINE_EXTENSION_TYPE,
	MULTI_BODIED_EXTENSION_TYPE,
	REGULAR_EXTENSION_TYPE,
} from '../../../extensionConstants.ts';

const INLINE_MACRO_OUTPUT_TYPE = 'INLINE';
const MAX_ADF_CONTENT_TRAVERSAL_DEPTH = 100;

// ADF block/container node types cannot be rendered inside an inline macro wrapper.
const INLINE_UNSAFE_ADF_BLOCK_NODE_TYPES = new Set([
	'blockquote',
	BODIED_EXTENSION_TYPE,
	'bulletList',
	'codeBlock',
	'decisionList',
	'expand',
	REGULAR_EXTENSION_TYPE,
	'heading',
	'layoutColumn',
	'layoutSection',
	'mediaGroup',
	'mediaSingle',
	MULTI_BODIED_EXTENSION_TYPE,
	'nestedExpand',
	'orderedList',
	'panel',
	'rule',
	'table',
	'taskList',
]);

type AdfLikeNode = {
	attrs?: {
		parameters?: {
			nestedContent?: unknown;
		} | null;
		extensionType?: unknown;
	} | null;
	content?: unknown;
	type?: unknown;
};

type ContentStackEntry = {
	content: unknown;
	depth: number;
};

const isAdfLikeNode = (value: unknown): value is AdfLikeNode =>
	Boolean(value && typeof value === 'object' && 'type' in value);

const pushArrayContent = (stack: ContentStackEntry[], content: unknown[], depth: number): void => {
	for (let index = content.length - 1; index >= 0; index -= 1) {
		stack.push({ content: content[index], depth });
	}
};

const isLegacyContentBlockExtension = (node: AdfLikeNode): boolean =>
	node.attrs?.extensionType === EXTENSION_TYPE.MIGRATION && node.type !== INLINE_EXTENSION_TYPE;

const isInlineUnsafeAdfBlockNode = (node: AdfLikeNode): boolean =>
	typeof node.type === 'string' && INLINE_UNSAFE_ADF_BLOCK_NODE_TYPES.has(node.type);

const hasSingleParagraph = (content: unknown): boolean =>
	Array.isArray(content) &&
	content.length === 1 &&
	isAdfLikeNode(content[0]) &&
	content[0].type === 'paragraph';

const containsInlineUnsafeContent = (initialContent: unknown): boolean => {
	const stack: ContentStackEntry[] = [{ content: initialContent, depth: 0 }];
	const visited = new WeakSet<object>();

	while (stack.length) {
		const entry = stack.pop();
		if (!entry) {
			continue;
		}

		const { content: currentContent, depth } = entry;

		if (!currentContent || typeof currentContent === 'string') {
			continue;
		}

		if (depth > MAX_ADF_CONTENT_TRAVERSAL_DEPTH) {
			return true;
		}

		if (Array.isArray(currentContent)) {
			pushArrayContent(stack, currentContent, depth);
			continue;
		}

		if (!isAdfLikeNode(currentContent)) {
			continue;
		}

		if (visited.has(currentContent)) {
			continue;
		}
		visited.add(currentContent);

		if (
			isLegacyContentBlockExtension(currentContent) ||
			isInlineUnsafeAdfBlockNode(currentContent)
		) {
			return true;
		}

		stack.push({ content: currentContent.content, depth: depth + 1 });
		stack.push({ content: currentContent.attrs?.parameters?.nestedContent, depth: depth + 1 });
	}

	return false;
};

/**
 * Determines if the extension should be displayed as inline based on the extension parameters.
 *
 * @param extensionParams - The extension parameters.
 * @returns True if the extension should be displayed as inline, false otherwise.
 */
export function shouldDisplayExtensionAsInline<T extends Parameters = Parameters>(
	extensionParams?: ExtensionParams<T>,
): boolean {
	// Always display excerpt as block
	if (extensionParams?.extensionKey === 'excerpt') {
		return false;
	}

	const isInlineBodiedLayout =
		// The layout kill switch must disable saved metadata independently of the existing macro rollout.
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		extensionParams?.type === BODIED_EXTENSION_TYPE &&
		extensionParams.extensionType === 'com.atlassian.ecosystem' &&
		extensionParams.parameters?.layout === 'inline-bodied' &&
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		fg('platform_forge_inline_bodied_layout_switch') &&
		// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
		fg('platform_forge_inline_bodied_macro');
	const content: unknown = extensionParams?.content;
	// Sibling composition supplies a ProseMirror Fragment; the extension wrapper supplies ADF.
	// Apply the same body-safety rules to both, without changing legacy macro traversal.
	const contentToInspect =
		isInlineBodiedLayout &&
		content &&
		typeof content === 'object' &&
		'toJSON' in content &&
		typeof content.toJSON === 'function'
			? content.toJSON()
			: content;
	if (isInlineBodiedLayout && !hasSingleParagraph(contentToInspect)) {
		return false;
	}
	if (containsInlineUnsafeContent(contentToInspect)) {
		return false;
	}
	if (isInlineBodiedLayout) {
		return true;
	}

	const macroParams = extensionParams?.parameters?.macroParams;

	// Connect / pre-migration shape: hyphenated key, value wrapped in { value }.
	if (macroParams?.['atlassian-macro-output-type']?.value === INLINE_MACRO_OUTPUT_TYPE) {
		return true;
	}

	// The camelCased shapes only reach the client on the Forge path.
	const guestParams = extensionParams?.parameters?.guestParams;
	return Boolean(
		// After Connect->Forge harmonisation the key is camelCased in macroParams
		macroParams?.['atlassianMacroOutputType']?.value === INLINE_MACRO_OUTPUT_TYPE ||
		// Flattened Forge guestParams shape: camelCased key, raw string value
		guestParams?.['atlassianMacroOutputType'] === INLINE_MACRO_OUTPUT_TYPE,
	);
}
